import type {AuditStructure, LoadAuditDataResponseItemBody} from '@dms/api/core';

type Category = AuditStructure['categories'][number];

export type ConditionArea = 'exterior' | 'interior' | 'technical';
export type ConditionStars = Record<ConditionArea, number | null>;

const AREA_KEYS: Record<ConditionArea, string> = {
  exterior: 'OVERALL_EVALUATION_OF_THE_EXTERIOR',
  interior: 'OVERALL_EVALUATION_OF_THE_INTERIOR',
  technical: 'OVERALL_EVALUATION_OF_THE_TECHNICAL_CONDITION',
};

const BEST_GRADE = 1;
const WORST_GRADE = 5;

function findCategory(categories: Category[], uniqueKey: string): Category | undefined {
  for (const category of categories) {
    if (category.uniqueKey === uniqueKey) {
      return category;
    }
    const child = findCategory(category.childCategories ?? [], uniqueKey);
    if (child) {
      return child;
    }
  }
  return undefined;
}

// The inspection grades the overall condition 1 (best) to 5 (worst); the buyer sees 5 to 1 stars.
export function inspectionRatings(
  structure: AuditStructure | undefined,
  audit: LoadAuditDataResponseItemBody | undefined
): ConditionStars {
  const stars = (area: ConditionArea) => {
    const category = structure && findCategory(structure.categories, AREA_KEYS[area]);
    const rating = structure?.ratings.find(({categoryId}) => categoryId === category?.id);
    const value = audit?.categories
      .find(({id}) => id === rating?.categoryId)
      ?.fields.find(
        ({paramDefinitionId}) => paramDefinitionId === rating?.paramDefinitionId
      )?.value;
    const grade = Number(value);
    return Number.isInteger(grade) && grade >= BEST_GRADE && grade <= WORST_GRADE
      ? WORST_GRADE + BEST_GRADE - grade
      : null;
  };
  return {exterior: stars('exterior'), interior: stars('interior'), technical: stars('technical')};
}
